import { openAuthModal } from '@/store/slices/auth-modal.slice';
import { useIsMobile } from '@/hooks';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { UserRole, LobbyTypeEnum } from '@sportspredict/shared';
import { ROUTES } from '@/constants';
import { buildAuthRedirectUrl } from '@/utils';
import { useLazyGetLobbiesByEventIdQuery } from '@/store/api';
import { useCleanupReferral } from './use-cleanup-referral.hook';

export function useAutoJoinFromSharedOnLobbyDetails({
	lobbyResponse,
	eventResponse,
	user,
	handleJoinLobby,
	setShowPasswordDialog,
	isEventClosed,
}: any) {
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const location = useLocation();
	const triedRef = useRef(false);
	const [pending, setPending] = useState(false);
	const [fetchLobbiesByEventId] = useLazyGetLobbiesByEventIdQuery();
	const dispatch = useDispatch();

	const cleanupBeforeRemove = useCleanupReferral();
	const isMobile = useIsMobile();
	const hasJoined = useMemo(
		() => !!lobbyResponse?.lobby_users?.some((u: any) => u.user_id === user?.id),
		[lobbyResponse?.lobby_users, user?.id]
	);

	const ready = !!lobbyResponse && !!eventResponse && user !== undefined;
	const isAdmin = user?.role === UserRole.Admin;

	useEffect(() => {
		const shared = !!searchParams.get('source');
		if (!shared) return;
		if (triedRef.current) return;
		if (user === undefined) return;
	}, [user, searchParams, navigate, location]);

	useEffect(() => {
		const shared = !!searchParams.get('source');
		const sourceKeyword = searchParams.get('source');

		if (!shared) return;
		if (triedRef.current || !ready || isEventClosed) return;

		triedRef.current = true;
		setPending(true);

		if (hasJoined || isAdmin) {
			setPending(false);
			return;
		}

		(async () => {
			try {
				if (!eventResponse?.has_multiple_lobbies) {
					const allLobbies = await fetchLobbiesByEventId(eventResponse.id).unwrap();
					const userExistingLobby = allLobbies?.find(
						(lobby: any) =>
							String(lobby.id) !== String(lobbyResponse.id) &&
							Array.isArray(lobby.lobby_users) &&
							lobby.lobby_users.some((u: any) => u.user_id === user.id)
					);

					if (userExistingLobby) {
						const isHeadToHead = userExistingLobby.type === LobbyTypeEnum.HeadToHead;
						const targetUrl = isHeadToHead
							? `/head-to-head/${eventResponse.id}/${userExistingLobby.id}`
							: `${ROUTES.LOBBY_DETAILS}?id=${userExistingLobby.id}&eventId=${eventResponse.id}`;
						await cleanupBeforeRemove();
						navigate(targetUrl, { replace: true });
						return;
					}
				}

				const isProtectedLobby =
					lobbyResponse?.is_private || lobbyResponse?.type === LobbyTypeEnum.CrowdLock;

				if (isProtectedLobby) {
					if (!user) {
						return isMobile ? dispatch(openAuthModal()) : navigate(buildAuthRedirectUrl());
					}
					await cleanupBeforeRemove();
					if (lobbyResponse?.is_private) {
						setShowPasswordDialog(true);
					}

					return;
				}

				await handleJoinLobby(null, sourceKeyword); // public -> join right away
				await cleanupBeforeRemove();
			} finally {
				setPending(false);
			}
		})();
	}, [
		ready,
		searchParams,
		hasJoined,
		isAdmin,
		lobbyResponse?.is_private,
		lobbyResponse?.id,
		eventResponse?.has_multiple_lobbies,
		eventResponse?.id,
		handleJoinLobby,
		setShowPasswordDialog,
		isEventClosed,
		navigate,
		user?.id,
		cleanupBeforeRemove,
		fetchLobbiesByEventId,
	]);

	return { autoJoinPending: pending };
}
